import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { usersAPI, peoplePlatformAPI, dashboardAPI } from '@/services';
import { useToast } from '../../components/Toast';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import MemberDashboard from '../../components/MemberDashboard';
import { memberController } from '../../controllers/memberController';
import { buildMemberDashboardPersonalStats } from '../../utils/memberDashboardPersonalStats';
import {
  harmonizedMemberUnpaidMonthsDisplay,
  unpaidMonthsCardClasses,
} from '../../utils/contributionArrearsUi';
import { digitsOnly } from '../../models/memberModel';
import ModalShell from '../../components/ModalShell';
import SessionHistoryList from '../../components/SessionHistoryList';
import {
  MemberMobileShell,
  MemberMobileHero,
  MemberMobileCollapse,
} from '../../components/MemberMobileShell';
import { useProfileShellOutletMerged } from '../profile/ProfileSectionShells';
import { uiLanguageLabel } from '../../constants/uiLanguages';
import { staffMembersBasePath, staffMembersListPath, staffUsersBasePath } from '../../utils/portalAdminNav';

function ConnexionField({ label, children, hint }) {
  return (
    <div className="rounded-xl border border-slate-200/90 bg-slate-50/40 px-3 py-2.5 md:px-4 md:py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <div className="mt-1 text-sm text-[#0f3350] font-medium break-words">{children}</div>
      {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

/** Cartes Total payé / Mois payés (cotisation mensuelle) / Mois impayés + dernier paiement (Synthèse & onglet Cotisations). */
function MemberContributionSummaryCards({ stats, formatCurrency, formatDateTime, unpaidMonthsHarmonized }) {
  const { t } = useTranslation('common');
  if (!stats) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 p-4 text-sm text-gray-600">
        {t('member.cotisationNoAggregatedStats')}
      </div>
    );
  }
  const total = Number(stats.total_contributions) || 0;
  const paidMonths = Number(stats.paid_months) || 0;
  const unpaidMonths =
    Number(unpaidMonthsHarmonized ?? stats.unpaid_months) || 0;
  const unpaidTone = unpaidMonthsCardClasses(unpaidMonths);
  const lastRaw = stats.last_payment_date || stats.last_payment;

  return (
    <div className="space-y-3 md:space-y-4">
      {/* Mobile : une seule bande compacte */}
      <div className="mm-card md:hidden overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100/90 px-3.5 py-3">
          <div className="min-w-0">
            <p className="font-display text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
              {t('member.cotisationTotalPaid')}
            </p>
            <p className="mt-0.5 font-display text-xl font-extrabold tabular-nums text-[#0f3350]">{formatCurrency(total)}</p>
          </div>
          <Link
            to="/contributions"
            className="mm-cta min-h-[36px] shrink-0 px-3 text-xs"
          >
            {t('member.viewContributions')}
          </Link>
        </div>
        <div className="grid grid-cols-2 divide-x divide-slate-100">
          <div className="px-3.5 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              {t('member.cotisationPaidMonths')}
            </p>
            <p className="mt-0.5 text-lg font-bold tabular-nums text-[#0f3350]">{paidMonths}</p>
          </div>
          <div className={`px-3.5 py-2.5 ${unpaidMonths > 0 ? 'bg-amber-50/50' : ''}`}>
            <p className={`text-[10px] font-semibold uppercase tracking-wide ${unpaidTone.label}`}>
              {t('member.cotisationUnpaidMonths')}
            </p>
            <p className={`mt-0.5 text-lg font-bold tabular-nums ${unpaidTone.value}`}>{unpaidMonths}</p>
          </div>
        </div>
        {lastRaw ? (
          <p className="border-t border-slate-100 px-3.5 py-2 text-[11px] text-slate-600">
            {t('member.cotisationLastPayment')} ·{' '}
            <span className="font-semibold text-slate-800">{formatDateTime(lastRaw)}</span>
          </p>
        ) : null}
      </div>

      {/* Desktop : cartes existantes */}
      <div className="hidden md:block space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-6">
        <div className="rounded-xl border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 p-4 md:p-6">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-xs font-medium text-green-700 md:text-sm">{t('member.cotisationTotalPaid')}</p>
              <p className="text-xl font-bold text-green-900 md:text-2xl">{formatCurrency(total)}</p>
            </div>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-500 text-lg font-bold text-white md:h-12 md:w-12 md:text-xl">
              {total > 0 ? (
                <svg className="h-5 w-5 md:h-6 md:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                '0'
              )}
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-entrea-panel p-4 md:p-6">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-xs font-medium text-blue-700 md:text-sm">{t('member.cotisationPaidMonths')}</p>
              <p className="text-[11px] font-normal text-blue-600/90 md:text-xs">{t('member.cotisationPaidMonthsHint')}</p>
              <p className="mt-0.5 text-xl font-bold text-blue-900 md:text-2xl">{paidMonths}</p>
            </div>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-500 text-lg font-bold text-white md:h-12 md:w-12 md:text-xl">
              {paidMonths}
            </div>
          </div>
        </div>
        <div
          className={`rounded-xl border ${unpaidTone.border} ${unpaidTone.bg} p-4 md:p-6`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className={`mb-1 text-xs font-medium md:text-sm ${unpaidTone.label}`}>{t('member.cotisationUnpaidMonths')}</p>
              <p className={`text-xl font-bold md:text-2xl ${unpaidTone.value}`}>{unpaidMonths}</p>
            </div>
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${unpaidTone.iconBg} text-lg text-white md:h-12 md:w-12 md:text-xl`}
            >
              <span aria-hidden>⏱</span>
            </div>
          </div>
        </div>
      </div>
      {lastRaw ? (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6">
          <p className="mb-1 text-xs font-medium text-gray-700 md:text-sm">{t('member.cotisationLastPayment')}</p>
          <p className="text-base font-semibold text-gray-900 md:text-lg">{formatDateTime(lastRaw)}</p>
        </div>
      ) : null}
      </div>
    </div>
  );
}

const MemberDetails = () => {
  const { t } = useTranslation('common');
  const outletCtx = useProfileShellOutletMerged();
  const profileShellMembre = outletCtx.profileShell === 'membre';
  const { id: routeMemberId } = useParams();
  const id = outletCtx.profileMemberId ?? routeMemberId;
  const navigate = useNavigate();
  const location = useLocation();
  const membersListPath = staffMembersListPath(location.pathname);
  const membersBase = staffMembersBasePath(location.pathname);
  const usersBase = staffUsersBasePath(location.pathname);
  const { error, success } = useToast();
  const { hasPermission } = usePermissions();
  const { user: currentUser } = useAuth();
  const isOwnProfile = useMemo(
    () =>
      !!id &&
      (String(currentUser?.member_id || '') === String(id) ||
        String(currentUser?.person_id || '') === String(id)),
    [id, currentUser?.member_id, currentUser?.person_id]
  );
  const [member, setMember] = useState(null);
  const [user, setUser] = useState(null);
  /** Compte IAM lié au profil People (affichage onglet connexion + fiche admin). */
  const [linkedIamUser, setLinkedIamUser] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('apercu');
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  /** Connexions IAM (profil personnel uniquement) — remplace l’historique People sur l’onglet Historique. */
  const [loginSessions, setLoginSessions] = useState([]);
  const [loginSessionsTotal, setLoginSessionsTotal] = useState(0);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [userEditData, setUserEditData] = useState({
    username: '',
    email: ''
  });
  const [saving, setSaving] = useState(false);
  const [dashboardGlobalStats, setDashboardGlobalStats] = useState(null);

  useEffect(() => {
    if (!id) return;
    loadMemberDetails();
  }, [id]);

  useEffect(() => {
    if (activeTab !== 'historique' || historyLoaded || !id) return;
    const uid = String(currentUser?.id || currentUser?.sub || '').trim();
    const useConnectionHistory = isOwnProfile && !!uid;
    let cancelled = false;
    (async () => {
      try {
        setHistoryLoading(true);
        if (useConnectionHistory) {
          const res = await usersAPI.getSessions(uid, { limit: 50, skip: 0 });
          if (cancelled) return;
          setLoginSessions(Array.isArray(res?.data?.sessions) ? res.data.sessions : []);
          setLoginSessionsTotal(Number(res?.data?.total_sessions) || 0);
          setHistoryEntries([]);
        } else {
          const res = await peoplePlatformAPI.getHistory(id, { limit: 100, skip: 0 });
          if (cancelled) return;
          const entries = Array.isArray(res?.data?.entries) ? res.data.entries : [];
          setHistoryEntries(entries);
          setLoginSessions([]);
          setLoginSessionsTotal(0);
        }
        setHistoryLoaded(true);
      } catch (err) {
        if (!cancelled) {
          console.error(useConnectionHistory ? 'Sessions:' : 'People history:', err);
          setHistoryEntries([]);
          setLoginSessions([]);
          setLoginSessionsTotal(0);
          setHistoryLoaded(true);
        }
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, historyLoaded, id, isOwnProfile, currentUser?.id, currentUser?.sub]);

  const loadMemberDetails = async () => {
    try {
      setLoading(true);
      const [detailPack, globalRes] = await Promise.all([
        memberController.fetchMemberDetails(id),
        profileShellMembre
          ? Promise.resolve({ data: {} })
          : dashboardAPI.getStats().catch(() => ({ data: {} })),
      ]);
      const { member: memberRow, linkedUser, contributions: rows, stats } = detailPack;
      setMember(memberRow);
      setContributions(rows);
      setStats(stats);
      setDashboardGlobalStats(globalRes?.data || {});
      setHistoryLoaded(false);
      setHistoryEntries([]);
      setLoginSessions([]);
      setLoginSessionsTotal(0);
      setLinkedIamUser(linkedUser || null);

      if (isOwnProfile && linkedUser) {
        setUser(linkedUser);
        setUserEditData({
          username: linkedUser.username || '',
          email: linkedUser.email || '',
        });
      } else {
        setUser(null);
      }
    } catch (err) {
      error('Erreur lors du chargement des détails du membre');
      console.error(err);
      navigate(profileShellMembre ? '/dashboard' : membersListPath);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!userEditData.username) {
      error('Le nom d\'utilisateur est obligatoire');
      return;
    }
    try {
      setSaving(true);
      const updateData = {
        username: userEditData.username,
        email: userEditData.email || null
      };
      
      await usersAPI.update(user.id, updateData);
      success('Profil mis à jour avec succès');
      
      setShowEditUserModal(false);
      // Recharger l'utilisateur
      const userResponse = await usersAPI.getByMemberId(id);
      const u = userResponse.data;
      if (u) {
        setUser(u);
        setLinkedIamUser(u);
        setUserEditData({
          username: u.username || '',
          email: u.email || '',
        });
      }
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleString('fr-FR', {
        dateStyle: 'long',
        timeStyle: 'short',
      });
    } catch {
      return String(dateString);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'FBU',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };
  const fullDisplayName =
    String(member?.last_name || '').trim() && String(member?.first_name || '').trim()
      ? `${String(member?.last_name || '').trim()}, ${String(member?.first_name || '').trim()}`
      : String(member?.name || '').trim() || 'Sans nom';

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const diffMonths = Math.floor(diffDays / 30);
      const diffYears = Math.floor(diffDays / 365);

      if (diffYears > 0) return `${diffYears} an${diffYears > 1 ? 's' : ''}`;
      if (diffMonths > 0) return `${diffMonths} mois`;
      return `${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    } catch {
      return '';
    }
  };

  const getTimeUntil = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = date - now;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const diffMonths = Math.floor(diffDays / 30);

      if (diffMonths > 0) return `dans ${diffMonths} mois`;
      if (diffDays > 0) return `dans ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
      return 'bientôt';
    } catch {
      return '';
    }
  };

  const dashboardPersonalStats = useMemo(() => {
    if (!id) return null;
    return buildMemberDashboardPersonalStats(
      id,
      contributions,
      stats,
      fullDisplayName,
      member?.member_number || ''
    );
  }, [id, contributions, stats, fullDisplayName, member?.member_number]);

  const harmonizedUnpaidMonths = useMemo(() => {
    if (dashboardPersonalStats) return harmonizedMemberUnpaidMonthsDisplay(dashboardPersonalStats);
    return Number(stats?.unpaid_months) || 0;
  }, [dashboardPersonalStats, stats?.unpaid_months]);

  const tabs = useMemo(
    () =>
      profileShellMembre
        ? [
            { id: 'apercu', name: 'Synthèse' },
            { id: 'cotisations', name: 'Cotisations' },
            { id: 'infos_personnels', name: 'Identité' },
            { id: 'contacts', name: 'Coord.' },
            { id: 'adhesion', name: 'Adhésion' },
            { id: 'infos_connexion', name: 'Compte' },
            { id: 'documents', name: 'Docs' },
            { id: 'historique', name: 'Sessions' },
          ]
        : [
            { id: 'apercu', name: 'Aperçu', icon: '📋' },
            { id: 'infos_personnels', name: 'Infos personnels', icon: '👤' },
            { id: 'adhesion', name: 'Adhésion', icon: '🎫' },
            { id: 'contacts', name: 'Contacts', icon: '📇' },
            { id: 'infos_connexion', name: 'Infos de connexion', icon: '🔐' },
            { id: 'cotisations', name: 'Cotisations', icon: '💰' },
            { id: 'documents', name: 'Documents', icon: '📎' },
            {
              id: 'historique',
              name: isOwnProfile ? 'Sessions' : 'Historique',
              icon: isOwnProfile ? '🔒' : '📜',
            },
          ],
    [profileShellMembre, isOwnProfile]
  );

  if (!id) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center px-4 py-12">
        <div className="entrea-portal-panel max-w-md p-6 text-center md:p-8">
          <p className="text-sm text-slate-700">{t('profilePage.noPerson')}</p>
          <Link to="/dashboard" className="entrea-btn-primary mt-5">
            {t('profilePage.backDashboard')}
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className={
          profileShellMembre
            ? 'flex min-h-[40vh] items-center justify-center py-16'
            : 'flex min-h-screen items-center justify-center bg-gray-50'
        }
      >
        <div className="text-center">
          <div className="mx-auto h-11 w-11 animate-spin rounded-full border-2 border-[#2f6fd4] border-t-transparent" />
          <p className="mt-4 text-sm text-slate-600">
            {profileShellMembre ? t('profilePage.loading') : t('loading')}
          </p>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div
        className={
          profileShellMembre
            ? 'flex min-h-[40vh] items-center justify-center px-4 py-12'
            : 'flex min-h-screen items-center justify-center bg-gray-50'
        }
      >
        <div className="entrea-portal-panel max-w-md p-6 text-center md:p-8">
          <p className="text-sm text-slate-700">
            {profileShellMembre ? t('profilePage.notFound') : 'Membre introuvable'}
          </p>
          <button
            type="button"
            onClick={() => navigate(profileShellMembre ? '/dashboard' : membersListPath)}
            className="entrea-btn-primary mt-5"
          >
            {profileShellMembre ? t('profilePage.backDashboard') : 'Retour à la liste'}
          </button>
        </div>
      </div>
    );
  }

  const memberPeopleEditPath = profileShellMembre ? '/profile/edit' : `${membersBase}/${member.id}/edit`;
  /** Ouvre la page d’édition sur l’onglet MemberEdit correspondant (#infos_personnels, …). */
  const navigateToMemberEditSection = (section) => {
    navigate({ pathname: memberPeopleEditPath, hash: section });
  };

  const photoFileId = String(member.photo_file_id || '').trim();
  const identityFileId = String(member.identity_document_file_id || '').trim();
  const docIdsRaw = Array.isArray(member.document_file_ids) ? member.document_file_ids.map((x) => String(x).trim()).filter(Boolean) : [];
  const complementaryDocIds = docIdsRaw.filter((fid) => fid !== photoFileId && fid !== identityFileId);

  const memberInitials = fullDisplayName
    ? fullDisplayName
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2)
    : 'M';

  const connexionUsernameMatchesPhone =
    !!linkedIamUser &&
    Boolean(digitsOnly(linkedIamUser.username)) &&
    digitsOnly(linkedIamUser.username) === digitsOnly(linkedIamUser.phone);

  const sh = profileShellMembre;
  const canEditPeople = hasPermission('members.edit') || (isOwnProfile && hasPermission('members.view_own'));

  return (
    <div
      className={
        sh
          ? 'min-h-[60vh] space-y-3 sm:space-y-4 md:space-y-5'
          : 'min-h-screen bg-gray-50'
      }
    >
      {/* Retour : liste membres (profil : lien tableau de bord déjà dans ProfileLayout) */}
      {!profileShellMembre && !isOwnProfile && (
        <div className="sticky top-0 z-20 border-b border-gray-200 bg-white px-4 py-3 shadow-sm md:px-6 md:py-4">
          <button
            type="button"
            onClick={() => navigate(membersListPath)}
            className="flex min-h-[44px] items-center text-gray-600 transition-colors hover:text-gray-900"
          >
            <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium md:text-base">Retour</span>
          </button>
        </div>
      )}

      <div className={`mx-auto max-w-7xl ${sh ? 'px-2.5 py-2 md:px-1 md:py-2' : 'px-3 py-4 md:px-6 md:py-8'}`}>
        {sh ? (
          <>
            <MemberMobileShell className="mb-1">
              <MemberMobileHero
                label={t('member.profileLabel')}
                title={fullDisplayName}
                hint={
                  [
                    member.member_number
                      ? t('profilePage.memberNumber', { number: member.member_number })
                      : null,
                    member.status === 'active'
                      ? t('profilePage.statusActive')
                      : t('profilePage.statusInactive'),
                  ]
                    .filter(Boolean)
                    .join(' · ')
                }
                tone={
                  member.status === 'active'
                    ? harmonizedUnpaidMonths > 0
                      ? 'warn'
                      : 'ok'
                    : 'warn'
                }
                actions={
                  <>
                    {canEditPeople ? (
                      <Link
                        to={{ pathname: memberPeopleEditPath, hash: 'infos_personnels' }}
                        className="mm-cta flex-1"
                      >
                        {t('profilePage.editSheet')}
                      </Link>
                    ) : null}
                    {harmonizedUnpaidMonths > 0 ? (
                      <Link
                        to="/contributions"
                        className="mm-cta mm-cta--ghost flex-1 border-amber-300 text-amber-950"
                      >
                        {t('member.regularize')}
                      </Link>
                    ) : (
                      <Link
                        to="/contributions"
                        className="mm-cta mm-cta--ghost flex-1"
                      >
                        {t('profilePage.quickContributions')}
                      </Link>
                    )}
                  </>
                }
              >
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#113a5b] text-sm font-bold text-white">
                    {memberInitials}
                  </div>
                  <div className="min-w-0 flex-1 text-xs text-slate-600">
                    <p className="truncate">{[member.email, member.phone].filter(Boolean).join(' · ') || '—'}</p>
                    {harmonizedUnpaidMonths > 0 ? (
                      <p className="mt-0.5 font-semibold text-amber-800">
                        {t('member.monthsUnpaid', { count: harmonizedUnpaidMonths })}
                      </p>
                    ) : (
                      <p className="mt-0.5 font-semibold text-emerald-800">{t('member.duesUpToDate')}</p>
                    )}
                  </div>
                </div>
              </MemberMobileHero>

              <div className="grid grid-cols-4 gap-1.5">
                {[
                  { id: 'apercu', label: 'Synthèse' },
                  { id: 'cotisations', label: 'Paiements' },
                  { id: 'infos_personnels', label: 'Identité' },
                  { id: 'infos_connexion', label: 'Compte' },
                ].map((q) => (
                  <button
                    key={q.id}
                    type="button"
                    onClick={() => setActiveTab(q.id)}
                    className={`mm-chip min-h-[40px] w-full px-1 ${
                      activeTab === q.id ? 'mm-chip--on' : 'mm-chip--off'
                    }`}
                  >
                    {q.label}
                  </button>
                ))}
              </div>

              <MemberMobileCollapse
                key={
                  ['contacts', 'adhesion', 'documents', 'historique'].includes(activeTab)
                    ? 'profile-more-open'
                    : 'profile-more'
                }
                title={t('member.otherSections')}
                defaultOpen={['contacts', 'adhesion', 'documents', 'historique'].includes(activeTab)}
              >
                <div className="grid grid-cols-2 gap-1.5 pt-1">
                  {[
                    { id: 'contacts', label: 'Coordonnées' },
                    { id: 'adhesion', label: 'Adhésion' },
                    { id: 'documents', label: 'Documents' },
                    { id: 'historique', label: 'Sessions' },
                  ].map((q) => (
                    <button
                      key={q.id}
                      type="button"
                      onClick={() => setActiveTab(q.id)}
                      className={`mm-chip min-h-[40px] w-full ${
                        activeTab === q.id ? 'mm-chip--on' : 'mm-chip--off'
                      }`}
                    >
                      {q.label}
                    </button>
                  ))}
                </div>
              </MemberMobileCollapse>
            </MemberMobileShell>

            <div className="mb-4 hidden md:mb-6 md:block">
              <div className="entrea-portal-trust">
                <div className="entrea-portal-trust__head flex flex-wrap items-center gap-5 px-6 py-6 md:gap-7 md:px-8 md:py-7">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-white/15 font-display text-lg font-extrabold tracking-tight text-white ring-1 ring-white/25 md:h-20 md:w-20 md:text-2xl">
                    {memberInitials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-[11px] font-bold uppercase tracking-[0.14em] text-white/70">
                      {t('member.profileLabel')}
                    </p>
                    <h1 className="mt-1 truncate font-display text-2xl font-extrabold tracking-tight text-white md:text-3xl">
                      {fullDisplayName}
                    </h1>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {member.member_number ? (
                        <span className="inline-flex items-center rounded-md bg-white/12 px-2.5 py-1 text-xs font-medium text-white ring-1 ring-white/20">
                          {t('profilePage.memberNumber', { number: member.member_number })}
                        </span>
                      ) : null}
                      <span
                        className={`inline-flex rounded-md px-2.5 py-1 text-xs font-semibold ${
                          member.status === 'active'
                            ? 'bg-emerald-400/20 text-emerald-100 ring-1 ring-emerald-300/30'
                            : 'bg-red-400/20 text-red-100 ring-1 ring-red-300/30'
                        }`}
                      >
                        {member.status === 'active'
                          ? t('profilePage.statusActive')
                          : t('profilePage.statusInactive')}
                      </span>
                      {harmonizedUnpaidMonths > 0 ? (
                        <span className="inline-flex rounded-md bg-amber-400/20 px-2.5 py-1 text-xs font-semibold text-amber-50 ring-1 ring-amber-300/30">
                          {t('member.monthsUnpaid', { count: harmonizedUnpaidMonths })}
                        </span>
                      ) : (
                        <span className="inline-flex rounded-md bg-white/10 px-2.5 py-1 text-xs font-medium text-white/90 ring-1 ring-white/15">
                          {t('profilePage.duesOk')}
                        </span>
                      )}
                    </div>
                    {(member.email || member.phone) && (
                      <p className="mt-2.5 text-sm text-white/80">
                        {[member.email, member.phone].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                  <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-col sm:items-stretch md:gap-2.5">
                    {canEditPeople ? (
                      <Link
                        to={{ pathname: memberPeopleEditPath, hash: 'infos_personnels' }}
                        className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-white px-4 text-sm font-semibold text-[#113a5b] shadow-sm transition hover:bg-slate-100"
                      >
                        {t('profilePage.editSheet')}
                      </Link>
                    ) : null}
                    <Link
                      to="/profile/compte"
                      className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-white/30 bg-white/10 px-4 text-sm font-semibold text-white transition hover:bg-white/15"
                    >
                      {t('profilePage.linkAccount')}
                    </Link>
                    <Link
                      to="/contributions"
                      className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-white/30 bg-transparent px-4 text-sm font-semibold text-white/95 transition hover:bg-white/10"
                    >
                      {t('profilePage.quickContributions')}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="mb-4 overflow-hidden rounded-2xl border-0 border-gray-200 bg-gradient-to-br from-white via-entrea-panel/35 to-entrea-surface/30 shadow-lg backdrop-blur-sm md:mb-6 md:rounded-xl md:border md:bg-white md:shadow-sm md:backdrop-blur-none">
              <div className="relative flex h-32 w-full items-center justify-center bg-gradient-to-br from-entrea-ink via-entrea-royal to-entrea-sky text-white shadow-lg md:h-40">
                <div className="text-5xl font-bold md:text-6xl">{memberInitials}</div>
                {canEditPeople && (
                  <button
                    type="button"
                    onClick={() => navigateToMemberEditSection('documents')}
                    className="absolute bottom-3 right-3 min-h-[40px] min-w-[40px] rounded-lg bg-white bg-opacity-90 p-2 text-entrea-royal shadow-lg transition-colors hover:bg-opacity-100"
                    title="Changer la photo"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                )}
              </div>

              <div className="p-4 md:p-8">
                <div className="min-w-0 flex-1 text-center md:text-left">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-4">
                    <div className="flex-1">
                      <h1 className="mb-1 text-xl font-bold text-gray-900 md:mb-2 md:text-3xl">{fullDisplayName}</h1>
                      <p className="mb-2 text-xs text-gray-500">Profil People</p>
                      {member.iamRole && (
                        <p className="mb-2 text-base text-gray-600 md:mb-4 md:text-lg">
                          <span className="text-sm text-gray-500">Rôle IAM · </span>
                          {member.iamRole}
                        </p>
                      )}
                      {member.member_number ? (
                        <p className="mb-2 text-sm font-semibold text-entrea-navy md:mb-3 md:text-base">
                          N° membre · <span className="font-mono tracking-wide">{member.member_number}</span>
                        </p>
                      ) : null}
                      {member.email && (
                        <p className="mb-2 break-all text-xs text-gray-500 md:mb-4 md:text-sm">{member.email}</p>
                      )}
                      {member.phone && <p className="text-xs text-gray-500 md:text-sm">{member.phone}</p>}
                    </div>
                    {isOwnProfile && user && (
                      <button
                        type="button"
                        onClick={() => setShowEditUserModal(true)}
                        className="flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-entrea-royal px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-colors hover:bg-entrea-navy md:px-6 md:py-3 md:text-base"
                      >
                        <svg className="h-4 w-4 md:h-5 md:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Modifier
                      </button>
                    )}
                    {!isOwnProfile && canEditPeople && (
                      <Link
                        to={{ pathname: memberPeopleEditPath, hash: 'infos_personnels' }}
                        className="flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-pink-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-colors hover:bg-pink-700 md:px-6 md:py-3 md:text-base"
                      >
                        <svg className="h-4 w-4 md:h-5 md:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Modifier
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky top-[57px] z-10 mb-4 rounded-xl border border-gray-200 bg-white shadow-sm md:top-0 md:mb-6">
              <div className="border-b border-gray-200">
                <nav
                  className="flex space-x-1 overflow-x-auto px-3 md:space-x-8 md:px-6 [&::-webkit-scrollbar]:hidden"
                  aria-label="Tabs"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`${
                        activeTab === tab.id
                          ? 'border-pink-500 text-pink-600'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      } flex min-w-fit flex-shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-3 text-xs font-medium transition-colors md:gap-2 md:px-1 md:py-4 md:text-sm`}
                    >
                      {tab.icon ? <span className="text-base md:text-lg">{tab.icon}</span> : null}
                      <span>{tab.name}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </>
        )}

        <div
          className={
            sh
              ? 'mt-2 flex flex-col gap-3 sm:mt-4 md:mt-6 md:flex-row md:items-start md:gap-6 lg:gap-8'
              : 'space-y-4 md:space-y-6'
          }
        >
          {sh ? (
            <aside className="hidden w-full shrink-0 md:block md:w-52 lg:w-56">
              <nav
                className="entrea-portal-panel md:sticky md:top-4"
                aria-label={t('profilePage.sectionsNav')}
              >
                <p className="border-b border-[#e6edf4] px-3 py-2.5 font-display text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                  {t('profilePage.sectionsNav')}
                </p>
                <ul className="flex flex-col gap-0.5 p-1.5">
                  {tabs.map((tab) => (
                    <li key={tab.id} className="w-full">
                      <button
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex w-full min-h-[40px] items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors ${
                          activeTab === tab.id
                            ? 'border-l-4 border-[#2f6fd4] bg-[#edf4ff] font-semibold text-[#1f6ab8]'
                            : 'border-l-4 border-transparent text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <span className="whitespace-normal">{tab.name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>
          ) : null}
          <div
            className={
              sh
                ? 'min-w-0 flex-1 space-y-3 sm:space-y-4 md:space-y-6 md:rounded md:border md:border-[#d9e1ea] md:bg-white md:p-8 md:shadow-sm'
                : 'contents'
            }
          >
          {activeTab === 'apercu' &&
            (sh ? (
              <div className="space-y-3 md:space-y-8">
                <div className="hidden border-b border-[#e6edf4] pb-5 md:block">
                  <h2 className="font-display text-lg font-extrabold tracking-tight text-[#0f3350]">
                    {t('profilePage.synthesis')}
                  </h2>
                </div>

                {/* Mobile : action due en tête */}
                {harmonizedUnpaidMonths > 0 ? (
                  <div className="md:hidden overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white px-3.5 py-3.5">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800/80">
                      À régulariser
                    </p>
                    <p className="mt-1 text-lg font-bold leading-snug text-amber-950">
                      {harmonizedUnpaidMonths} mois impayé{harmonizedUnpaidMonths > 1 ? 's' : ''}
                    </p>
                    <Link
                      to="/contributions?invoice=arrears"
                      className="mt-3 flex min-h-[44px] items-center justify-center rounded-xl bg-amber-900 px-3 text-sm font-semibold text-white"
                    >
                      Voir et payer
                    </Link>
                  </div>
                ) : null}

                <MemberContributionSummaryCards
                  stats={stats}
                  formatCurrency={formatCurrency}
                  formatDateTime={formatDateTime}
                  unpaidMonthsHarmonized={harmonizedUnpaidMonths}
                />

                <div className="md:hidden space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Link
                      to="/contributions"
                      className="mm-cta mm-cta--ghost"
                    >
                      Cotisations
                    </Link>
                    {canEditPeople ? (
                      <Link
                        to={{ pathname: memberPeopleEditPath, hash: 'infos_personnels' }}
                        className="mm-cta mm-cta--ghost"
                      >
                        Modifier la fiche
                      </Link>
                    ) : isOwnProfile && user ? (
                      <button
                        type="button"
                        onClick={() => setShowEditUserModal(true)}
                        className="mm-cta mm-cta--ghost"
                      >
                        Identifiants
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setActiveTab('infos_personnels')}
                        className="mm-cta mm-cta--ghost"
                      >
                        Identité
                      </button>
                    )}
                  </div>

                  <MemberMobileCollapse title="Fiche membre" defaultOpen={false}>
                    <dl className="grid grid-cols-2 gap-3 pt-1">
                      <div>
                        <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Statut</dt>
                        <dd
                          className={`mt-0.5 text-sm font-semibold ${
                            member.status === 'active' ? 'text-emerald-700' : 'text-red-600'
                          }`}
                        >
                          {member.status === 'active' ? 'Actif' : 'Inactif'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Type</dt>
                        <dd className="mt-0.5 truncate text-sm font-medium text-[#0f3350]">{member.type || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Depuis</dt>
                        <dd className="mt-0.5 text-sm font-medium text-[#0f3350]">{formatDateShort(member.join_date)}</dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Groupe</dt>
                        <dd className="mt-0.5 truncate text-sm font-medium text-[#0f3350]">
                          {member.groups?.[0] || member.commission || '—'}
                        </dd>
                      </div>
                      {member.profession ? (
                        <div className="col-span-2">
                          <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Profession</dt>
                          <dd className="mt-0.5 text-sm font-medium text-[#0f3350]">{member.profession}</dd>
                        </div>
                      ) : null}
                      {member.nationality ? (
                        <div className="col-span-2">
                          <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Nationalité</dt>
                          <dd className="mt-0.5 text-sm font-medium text-[#0f3350]">{member.nationality}</dd>
                        </div>
                      ) : null}
                    </dl>
                  </MemberMobileCollapse>
                </div>

                {/* Desktop meta */}
                <dl className="hidden grid-cols-2 gap-6 border-t border-gray-200 pt-8 sm:grid-cols-4 md:grid">
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Statut</dt>
                    <dd
                      className={`mt-1 text-sm font-semibold ${
                        member.status === 'active' ? 'text-emerald-700' : 'text-red-600'
                      }`}
                    >
                      {member.status === 'active' ? 'Actif' : 'Inactif'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Type People</dt>
                    <dd className="mt-1 truncate text-sm font-medium text-gray-900">{member.type || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Membre depuis</dt>
                    <dd className="mt-1 text-sm font-medium text-gray-900">{formatDateShort(member.join_date)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Groupe</dt>
                    <dd className="mt-1 truncate text-sm font-medium text-gray-900">
                      {member.groups?.[0] || member.commission || '—'}
                    </dd>
                  </div>
                </dl>
                {(member.profession || member.nationality) && (
                  <div className="hidden gap-6 border-t border-gray-200 pt-8 sm:grid-cols-2 md:grid">
                    {member.profession ? (
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Profession</p>
                        <p className="mt-1 text-sm font-medium text-gray-900">{member.profession}</p>
                      </div>
                    ) : null}
                    {member.nationality ? (
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Nationalité</p>
                        <p className="mt-1 text-sm font-medium text-gray-900">{member.nationality}</p>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4 md:space-y-6">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6">
                  <div className="mb-4">
                    <h3 className="text-base font-bold text-gray-900 md:text-lg">Aperçu</h3>
                  </div>
                  <div className="mb-4 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4 md:gap-3">
                    <div className="rounded-lg bg-gray-50 p-3">
                      <p className="text-xs uppercase text-gray-500">Statut</p>
                      <p className={`font-semibold ${member.status === 'active' ? 'text-green-600' : 'text-red-600'}`}>
                        {member.status === 'active' ? 'Actif' : 'Inactif'}
                      </p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3">
                      <p className="text-xs uppercase text-gray-500">Type People</p>
                      <p className="truncate font-medium text-gray-900">{member.type || '—'}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3">
                      <p className="text-xs uppercase text-gray-500">Membre depuis</p>
                      <p className="font-medium text-gray-900">{formatDateShort(member.join_date)}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3">
                      <p className="text-xs uppercase text-gray-500">Groupe</p>
                      <p className="truncate font-medium text-gray-900">
                        {member.groups?.[0] || member.commission || '—'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 bg-slate-50/80 p-3 md:p-5">
                  <p className="mb-4 text-sm text-gray-600">
                    Tableau de bord membre (même présentation que l&apos;accueil membre). Les liens ouvrent les pages
                    habituelles du portail.
                  </p>
                  {dashboardPersonalStats && (
                    <MemberDashboard
                      personalStats={dashboardPersonalStats}
                      globalStats={dashboardGlobalStats || {}}
                    />
                  )}
                </div>
              </div>
            ))}

          {activeTab === 'infos_personnels' && (
            <div
              className={
                sh
                  ? 'mm-card space-y-4 p-3.5 md:space-y-6 md:rounded-none md:border-0 md:bg-transparent md:p-0 md:shadow-none md:[backdrop-filter:none]'
                  : 'rounded-2xl border-0 border-gray-200 bg-gradient-to-br from-white via-entrea-panel/35 to-entrea-surface/30 p-4 shadow-lg backdrop-blur-sm md:rounded-xl md:border md:bg-white md:shadow-sm md:backdrop-blur-none md:p-6'
              }
            >
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h3 className="text-base md:text-lg font-bold text-gray-900">Infos personnels</h3>
                {canEditPeople && (
                  <button
                    type="button"
                    onClick={() => navigateToMemberEditSection('infos_personnels')}
                    className="px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-entrea-royal hover:text-entrea-ink border border-entrea-sky/40 rounded-lg hover:bg-entrea-panel transition-colors min-h-[36px] md:min-h-[40px]"
                  >
                    Modifier
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Prénom</label>
                  <p className="mt-1 text-sm text-gray-900">{member.first_name || '—'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Nom</label>
                  <p className="mt-1 text-sm text-gray-900">{member.last_name || '—'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Post-nom</label>
                  <p className="mt-1 text-sm text-gray-900">{member.middle_name || '—'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Date de naissance</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {member.date_of_birth ? formatDateShort(member.date_of_birth) : '—'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Lieu de naissance</label>
                  <p className="mt-1 text-sm text-gray-900">{member.place_of_birth || '—'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Sexe</label>
                  <p className="mt-1 text-sm text-gray-900">{member.sex || '—'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">État civil</label>
                  <p className="mt-1 text-sm text-gray-900">{member.marital_status || '—'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Nationalité</label>
                  <p className="mt-1 text-sm text-gray-900">{member.nationality || '—'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Profession</label>
                  <p className="mt-1 text-sm text-gray-900">{member.profession || '—'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Langue de l&apos;interface</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {member.preferred_language ? uiLanguageLabel(member.preferred_language) : '—'}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">Alignée sur les langues disponibles dans l&apos;application.</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Type de pièce</label>
                  <p className="mt-1 text-sm text-gray-900">{member.identity_document_type || '—'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Numéro de pièce</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {member.identity_document_number || member.id_number || '—'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Expiration pièce</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {member.identity_document_expiration_date
                      ? formatDateShort(member.identity_document_expiration_date)
                      : '—'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'adhesion' && (
            <div
              className={
                sh
                  ? 'mm-card space-y-4 p-3.5 md:space-y-0 md:divide-y md:divide-gray-200 md:rounded-none md:border-0 md:bg-transparent md:p-0 md:shadow-none md:[backdrop-filter:none]'
                  : 'space-y-4 md:space-y-6'
              }
            >
              <div
                className={
                  sh ? 'md:pb-8 md:pt-0' : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6'
                }
              >
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <h3 className="text-base md:text-lg font-bold text-gray-900">Adhésion</h3>
                  {canEditPeople && !isOwnProfile && (
                    <button
                      type="button"
                      onClick={() => navigateToMemberEditSection('adhesion')}
                      className="px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-entrea-royal hover:text-entrea-ink border border-entrea-sky/40 rounded-lg hover:bg-entrea-panel transition-colors min-h-[36px] md:min-h-[40px]"
                    >
                      Modifier
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase">Statut d&apos;adhésion</label>
                    <p className={`mt-1 text-lg font-bold ${member.status === 'active' ? 'text-green-600' : 'text-red-600'}`}>
                      {member.status === 'active' ? 'Actif' : 'Inactif'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase">N° membre</label>
                    <p className="mt-1 text-2xl font-bold tracking-wide text-entrea-royal font-mono">
                      {member.member_number || '—'}
                    </p>
                    <p className="mt-2 hidden text-xs text-gray-500 md:block">
                      Identifiant technique (support){' '}
                      <span className="font-mono text-gray-700 break-all">{member.id}</span>
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase">Membre depuis</label>
                    <p className="mt-1 text-lg font-bold text-gray-900">
                      {formatDate(member.join_date)}
                      {member.join_date && (
                        <span className="text-sm text-gray-500 ml-2">({getTimeAgo(member.join_date)})</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase">Date de création</label>
                    <p className="mt-1 text-lg font-bold text-gray-900">{formatDate(member.created_at)}</p>
                  </div>
                  {(member.groups?.length > 0 || member.commission) && (
                    <div className="md:col-span-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase">Groupes / commission</label>
                      <p className="mt-1 text-lg font-bold text-gray-900">
                        {(member.groups || []).join(', ') || member.commission || '—'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div
                className={
                  sh ? 'py-8' : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6'
                }
              >
                <h4 className="text-sm font-bold text-gray-900 uppercase mb-3">Rôle IAM</h4>
                <div className="flex flex-wrap gap-2">
                  {member.iamRole ? (
                    <span className="px-3 py-1 bg-entrea-panel text-entrea-navy rounded-full text-sm font-medium">
                      {member.iamRole}
                    </span>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Aucun rôle IAM lié à ce profil</p>
                  )}
                </div>
              </div>

              <div className={sh ? 'pt-8' : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6'}>
                <h4 className="text-sm font-bold text-gray-900 uppercase mb-2">Groupes</h4>
                {!sh ? (
                  <p className="text-xs text-gray-500 mb-3">
                    People (<code className="text-xs">metadata.groups</code>) et groupes IAM (
                    <code className="text-xs">group_ids</code>) lorsque l&apos;utilisateur est lié.
                  </p>
                ) : (
                  <p className="mb-3 text-xs text-gray-500">Groupes People et références IAM.</p>
                )}
                <div className="flex flex-wrap gap-2">
                  {(member.groups || []).map((g) => (
                    <span key={g} className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                      {g}
                    </span>
                  ))}
                  {(member.iamGroupIds || []).map((gid) => (
                    <span
                      key={gid}
                      className="px-3 py-1 bg-entrea-panel text-entrea-ink rounded-full text-sm font-medium border border-entrea-sky/40"
                      title="Groupe IAM"
                    >
                      IAM · {String(gid).slice(0, 14)}
                      {String(gid).length > 14 ? '…' : ''}
                    </span>
                  ))}
                </div>
                {(!member.groups || member.groups.length === 0) &&
                  (!member.iamGroupIds || member.iamGroupIds.length === 0) && (
                    <p className="text-sm text-gray-500 italic mt-2">Aucun groupe assigné</p>
                  )}
              </div>
            </div>
          )}

          {activeTab === 'contacts' && (
            <div
              className={
                sh
                  ? 'mm-card space-y-4 p-3.5 md:space-y-6 md:rounded-none md:border-0 md:bg-transparent md:p-0 md:shadow-none md:[backdrop-filter:none]'
                  : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6'
              }
            >
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h3 className="text-base md:text-lg font-bold text-gray-900">Contacts</h3>
                {canEditPeople && (
                  <button
                    type="button"
                    onClick={() => navigateToMemberEditSection('contacts')}
                    className="px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-entrea-royal hover:text-entrea-ink border border-entrea-sky/40 rounded-lg hover:bg-entrea-panel transition-colors min-h-[36px] md:min-h-[40px]"
                  >
                    Modifier
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">E-mail</label>
                  <p className="mt-1 text-sm text-gray-900 break-all">{member.email || '—'}</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Téléphone</label>
                  <p className="mt-1 text-sm text-gray-900">{member.phone || '—'}</p>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Adresse</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {[
                      member.address_line1,
                      member.address_line2,
                      [member.address_postal_code, member.address_city].filter(Boolean).join(' ').trim(),
                      member.address_country,
                    ]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'infos_connexion' && (
              <div
                className={
                  sh
                    ? 'mm-card space-y-4 p-3.5 md:space-y-5 md:rounded-none md:border-0 md:bg-transparent md:p-0 md:shadow-none md:[backdrop-filter:none]'
                    : 'space-y-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6'
                }
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="text-base md:text-lg font-bold text-gray-900">
                      {sh ? 'Compte' : 'Informations de connexion'}
                    </h3>
                    <p className="mt-1 max-w-xl text-xs text-gray-500 md:text-sm">
                      {sh
                        ? 'Identifiants IAM pour vous connecter.'
                        : 'Identifiants du compte IAM utilisé pour la connexion à Entrea. Les coordonnées People (onglet Contacts) peuvent être différentes.'}
                    </p>
                  </div>
                  {isOwnProfile && user && (
                    <button
                      type="button"
                      onClick={() => setShowEditUserModal(true)}
                      className="px-4 py-2.5 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition-colors font-semibold flex items-center justify-center gap-2 shadow-md min-h-[44px] text-sm shrink-0"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Modifier identifiant / email
                    </button>
                  )}
                </div>

                {linkedIamUser ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <ConnexionField
                        label="Nom d'utilisateur"
                        hint={
                          connexionUsernameMatchesPhone
                            ? 'Identique au téléphone : connexion par numéro.'
                            : undefined
                        }
                      >
                        <span className="font-mono text-[0.95rem]">{linkedIamUser.username || '—'}</span>
                      </ConnexionField>
                      <ConnexionField
                        label="Email"
                        hint={isOwnProfile && user && !String(linkedIamUser.email || '').trim() ? 'Vous pouvez l’ajouter via « Modifier ».' : undefined}
                      >
                        {String(linkedIamUser.email || '').trim() ? (
                          <a href={`mailto:${linkedIamUser.email}`} className="text-entrea-royal hover:underline break-all">
                            {linkedIamUser.email}
                          </a>
                        ) : (
                          <span className="text-slate-400 italic font-normal">Non renseigné</span>
                        )}
                      </ConnexionField>
                      <ConnexionField
                        label="Téléphone (IAM)"
                        hint={connexionUsernameMatchesPhone ? 'Utilisé aussi comme identifiant de connexion.' : undefined}
                      >
                        {linkedIamUser.phone ? (
                          <span className="font-mono text-[0.95rem]">{linkedIamUser.phone}</span>
                        ) : (
                          <span className="text-slate-400 italic font-normal">—</span>
                        )}
                      </ConnexionField>
                      <ConnexionField label="Rôle IAM">
                        <span className="capitalize">{linkedIamUser.role || '—'}</span>
                      </ConnexionField>
                    </div>

                    <div
                      className={`flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between ${
                        sh
                          ? 'rounded-xl border border-gray-200 bg-gray-50'
                          : 'rounded-xl border border-entrea-panel bg-entrea-panel/40'
                      }`}
                    >
                      <p className="text-sm text-gray-700">
                        Mot de passe, code PIN et sessions : page dédiée du compte IAM.
                      </p>
                      <Link
                        to={sh && isOwnProfile ? '/profile/compte' : `${usersBase}/${linkedIamUser.id}`}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-entrea-royal px-4 py-2.5 text-sm font-semibold text-white hover:bg-entrea-navy transition-colors min-h-[44px] shrink-0"
                      >
                        Ouvrir la sécurité du compte
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </Link>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-gray-600 py-4">
                    Aucun compte IAM n’est lié à ce profil People. Un administrateur peut associer un utilisateur
                    depuis la fiche utilisateur.
                  </p>
                )}

                {!isOwnProfile && linkedIamUser && hasPermission('users.edit') && (
                  <p className="text-sm">
                    <Link to={`${usersBase}/${linkedIamUser.id}`} className="font-medium text-entrea-royal hover:text-entrea-navy">
                      Voir la fiche utilisateur (admin) →
                    </Link>
                  </p>
                )}
              </div>
          )}

          {activeTab === 'cotisations' && (
            <div className="space-y-3 md:space-y-6">
              <div className="hidden flex-col gap-3 sm:flex-row sm:items-center sm:justify-between md:flex">
                <h3 className="text-base font-bold text-gray-900 md:text-lg">Cotisations</h3>
                <div className="flex flex-wrap gap-2">
                  {harmonizedUnpaidMonths > 0 &&
                  (isOwnProfile || (member && String(member.status || '').toLowerCase() === 'active')) ? (
                    <Link
                      to={
                        isOwnProfile
                          ? '/contributions?invoice=arrears'
                          : `${location.pathname.startsWith('/portal') ? '/portal/contributions' : '/contributions'}?member=${encodeURIComponent(id)}&invoice=arrears`
                      }
                      className="inline-flex min-h-[40px] items-center justify-center rounded-lg border border-amber-300/80 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-950 transition-colors hover:bg-amber-100"
                    >
                      {t('contributionsPage.invoice.openArrearsShort')}
                    </Link>
                  ) : null}
                  {isOwnProfile ? (
                    <Link
                      to="/contributions"
                      className="inline-flex min-h-[40px] items-center justify-center rounded-lg border border-entrea-sky/40 bg-white px-4 py-2 text-sm font-medium text-entrea-royal transition-colors hover:bg-entrea-panel"
                    >
                      Payer / historique
                    </Link>
                  ) : null}
                  {canEditPeople && !isOwnProfile ? (
                    <button
                      type="button"
                      onClick={() => navigateToMemberEditSection('cotisations')}
                      className="inline-flex min-h-[40px] items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-[#0f3350] transition hover:bg-gray-50"
                    >
                      Modifier la fiche
                    </button>
                  ) : null}
                </div>
              </div>

              {sh && isOwnProfile ? (
                <div className="md:hidden space-y-3">
                  {harmonizedUnpaidMonths > 0 ? (
                    <div className="overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white px-3.5 py-3.5">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800/80">
                        Retard
                      </p>
                      <p className="mt-1 text-lg font-bold text-amber-950">
                        {harmonizedUnpaidMonths} mois · à régulariser
                      </p>
                      <Link
                        to="/contributions?invoice=arrears"
                        className="mt-3 flex min-h-[44px] items-center justify-center rounded-xl bg-amber-900 px-3 text-sm font-semibold text-white"
                      >
                        Régulariser maintenant
                      </Link>
                    </div>
                  ) : (
                    <Link
                      to="/contributions"
                      className="mm-cta"
                    >
                      Gérer mes cotisations
                    </Link>
                  )}
                </div>
              ) : null}

              <MemberContributionSummaryCards
                stats={stats}
                formatCurrency={formatCurrency}
                formatDateTime={formatDateTime}
                unpaidMonthsHarmonized={harmonizedUnpaidMonths}
              />

              {/* Historique mobile : replié */}
              {sh ? (
                <div className="md:hidden">
                  <MemberMobileCollapse
                    title={`Historique · ${contributions.length}`}
                    defaultOpen={contributions.length > 0 && contributions.length <= 3}
                  >
                    {contributions.length === 0 ? (
                      <p className="py-2 text-sm text-slate-500">Aucune cotisation enregistrée</p>
                    ) : (
                      <ul className="divide-y divide-slate-100">
                        {contributions.slice(0, 12).map((contribution) => (
                          <li key={contribution.id} className="flex items-center justify-between gap-2 py-2.5">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-[#0f3350]">
                                {contribution.month || formatDate(contribution.created_at)}
                              </p>
                              <p className="text-[11px] text-slate-500">
                                {contribution.status === 'paid'
                                  ? t('member.cotisationRowStatusPaid')
                                  : contribution.status === 'pending'
                                    ? t('member.cotisationRowStatusPending')
                                    : contribution.status === 'rejected'
                                      ? t('member.cotisationRowStatusRejected')
                                      : contribution.status || '—'}
                              </p>
                            </div>
                            <p className="shrink-0 text-sm font-bold tabular-nums text-[#0f3350]">
                              {formatCurrency(contribution.amount)}
                            </p>
                          </li>
                        ))}
                      </ul>
                    )}
                    {isOwnProfile && contributions.length > 0 ? (
                      <Link
                        to="/contributions"
                        className="mt-2 flex min-h-[40px] items-center justify-center rounded-xl border border-slate-200 text-xs font-semibold text-[#113a5b]"
                      >
                        Voir tout
                      </Link>
                    ) : null}
                  </MemberMobileCollapse>
                </div>
              ) : null}

              <div className={`${sh ? 'hidden md:block' : ''} bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6`}>
                <h3 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4">Historique des cotisations</h3>
                {contributions.length === 0 ? (
                  <div className="text-center py-8 md:py-12 text-gray-500 text-sm md:text-base">
                    Aucune cotisation enregistrée
                  </div>
                ) : (
                  <>
                    <div className="md:hidden space-y-2">
                      {contributions.map((contribution) => (
                        <div key={contribution.id} className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2.5">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-[#0f3350]">
                                {contribution.month || formatDate(contribution.created_at)}
                              </p>
                              <p className="mt-0.5 text-[11px] text-slate-500">
                                {contribution.payment_method || contribution.notes || '—'}
                              </p>
                            </div>
                            <div className="shrink-0 text-right">
                              <p className="text-sm font-bold tabular-nums text-[#0f3350]">
                                {formatCurrency(contribution.amount)}
                              </p>
                              <span
                                className={`mt-0.5 inline-flex rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
                                  contribution.status === 'completed'
                                    ? 'bg-green-100 text-green-700'
                                    : contribution.status === 'pending'
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {contribution.status === 'completed'
                                  ? t('member.cotisationRowStatusPaid')
                                  : contribution.status === 'pending'
                                    ? t('member.cotisationRowStatusPending')
                                    : t('member.cotisationRowStatusRejected')}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="hidden md:block overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Période
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Montant
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Statut
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Méthode de paiement
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {contributions.map((contribution) => (
                            <tr key={contribution.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {contribution.month || formatDate(contribution.created_at)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {formatCurrency(contribution.amount)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span
                                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    contribution.status === 'completed'
                                      ? 'bg-green-100 text-green-800'
                                      : contribution.status === 'pending'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-red-100 text-red-800'
                                  }`}
                                >
                                  {contribution.status === 'completed'
                                    ? t('member.cotisationRowStatusPaid')
                                    : contribution.status === 'pending'
                                      ? t('member.cotisationRowStatusPending')
                                      : t('member.cotisationRowStatusRejected')}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                {contribution.payment_method || contribution.notes || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div
              className={
                sh
                  ? 'mm-card space-y-4 p-3.5 md:space-y-6 md:rounded-none md:border-0 md:bg-transparent md:p-0 md:shadow-none md:[backdrop-filter:none]'
                  : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6'
              }
            >
              <div className="flex items-center justify-between mb-3 md:mb-4">
                <h3 className="text-base md:text-lg font-bold text-gray-900">Documents</h3>
                {canEditPeople && (
                  <button
                    type="button"
                    onClick={() => navigateToMemberEditSection('documents')}
                    className="px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-entrea-royal hover:text-entrea-ink border border-entrea-sky/40 rounded-lg hover:bg-entrea-panel transition-colors min-h-[36px] md:min-h-[40px]"
                  >
                    Modifier
                  </button>
                )}
              </div>
              <div className="space-y-6">
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Photo (fichier)</h4>
                  <p className="text-sm font-mono text-gray-900 break-all">{photoFileId || '—'}</p>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Pièce d&apos;identité (fichier)</h4>
                  <p className="text-sm font-mono text-gray-900 break-all">{identityFileId || '—'}</p>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Documents complémentaires</h4>
                  {complementaryDocIds.length === 0 ? (
                    <p className="text-sm text-gray-500">Aucun autre fichier référencé sur la fiche People.</p>
                  ) : (
                    <ul className="list-disc list-inside text-sm font-mono text-gray-900 space-y-1">
                      {complementaryDocIds.map((fid) => (
                        <li key={fid} className="break-all">
                          {fid}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'historique' && (
            <div className={sh ? 'space-y-4' : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6'}>
              <h3 className="text-base md:text-lg font-bold text-gray-900 mb-3 md:mb-4">
                {isOwnProfile ? 'Sessions' : 'Historique People'}
              </h3>
              {isOwnProfile ? (
                <p className="mb-4 text-xs text-gray-500">
                  Connexions enregistrées sur votre compte (données issues des journaux de connexion).
                </p>
              ) : null}
              {isOwnProfile ? (
                <SessionHistoryList
                  sessions={loginSessions}
                  totalSessions={loginSessionsTotal}
                  formatDateTime={formatDateTime}
                  loading={historyLoading}
                />
              ) : null}
              {historyLoading && !isOwnProfile && (
                <div className="flex items-center gap-2 text-sm text-gray-600 py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-entrea-royal" />
                  Chargement…
                </div>
              )}
              {!historyLoading && !isOwnProfile && historyEntries.length === 0 && (
                <p className="text-sm text-gray-500">Aucune entrée d&apos;historique métier pour cette fiche.</p>
              )}
              {!historyLoading && !isOwnProfile && historyEntries.length > 0 && (
                <ul className="space-y-3">
                  {historyEntries.map((entry) => (
                    <li key={entry._id} className="text-sm border-l-2 border-pink-200 pl-3 py-1">
                      <span className="font-medium text-gray-900">{entry.action}</span>
                      <span className="text-gray-400 mx-2">·</span>
                      <span className="text-gray-500">
                        {entry.timestamp ? new Date(entry.timestamp).toLocaleString('fr-FR') : '—'}
                      </span>
                      {entry.actor_user_id && (
                        <p className="text-xs text-gray-500 mt-1">
                          Acteur : <span className="font-mono">{entry.actor_user_id}</span>
                        </p>
                      )}
                      {entry.fields_changed?.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">{entry.fields_changed.join(', ')}</p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Modal pour modifier le profil utilisateur */}
      {showEditUserModal && user && (
        <ModalShell
          maxWidthClass="max-w-md"
          panelClassName="flex min-h-0 flex-col overflow-hidden p-0"
          onBackdropClick={() => {
            setShowEditUserModal(false);
            setUserEditData({
              username: user.username || '',
              email: user.email || '',
              phone: user.phone || '',
            });
          }}
        >
          <div className="mm-modal-header">
            <div className="min-w-0">
              <p className="mm-hero__label text-[#1f6ab8]">Profil</p>
              <h3 className="mt-0.5 font-display text-lg font-extrabold tracking-tight text-[#0f3350]">
                Modifier mon profil
              </h3>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowEditUserModal(false);
                setUserEditData({
                  username: user.username || '',
                  email: user.email || '',
                  phone: user.phone || '',
                });
              }}
              className="mm-press flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100/80 text-slate-500"
              aria-label="Annuler"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-3 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4">
            <div className="mm-card space-y-3 p-3 sm:p-3.5">
              <div>
                <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                  Nom d&apos;utilisateur *
                </label>
                <input
                  type="text"
                  value={userEditData.username}
                  onChange={(e) => setUserEditData({ ...userEditData, username: e.target.value })}
                  className="mm-field"
                  required
                  placeholder="Nom d'utilisateur"
                />
                <p className="mt-1.5 text-xs text-slate-500">
                  Le nom d&apos;utilisateur correspond généralement à votre numéro de téléphone
                </p>
              </div>
              <div>
                <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                  Email
                </label>
                <input
                  type="email"
                  value={userEditData.email || ''}
                  onChange={(e) => setUserEditData({ ...userEditData, email: e.target.value })}
                  className="mm-field"
                  placeholder="exemple@email.com"
                />
              </div>
            </div>
            <div className="rounded-xl border border-[#bcd4f6] bg-gradient-to-r from-[#edf4ff] to-white px-3 py-2.5 text-sm text-[#0f3350]">
              <strong className="font-bold">Note :</strong> Pour modifier votre numéro de téléphone,
              veuillez contacter un administrateur. Cette modification nécessite une approbation.
            </div>
          </div>

          <div className="mm-modal-footer">
            <button
              type="button"
              onClick={() => {
                setShowEditUserModal(false);
                setUserEditData({
                  username: user.username || '',
                  email: user.email || '',
                });
              }}
              className="mm-cta mm-cta--ghost flex-1"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleUpdateUser}
              disabled={saving}
              className="mm-cta flex-1 disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </ModalShell>
      )}

    </div>
  );
};

export default MemberDetails;
